import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { MediaImage } from '@elfsight/widget-media';
import { Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useCheckoutColors } from '../providers';

const COMPONENT_NAME = 'product-card';

export interface ProductCardProps {
  label?: string;
  description?: string;
  imageSrc?: string;
}

export function ProductCard({
  label,
  description,
  imageSrc
}: ProductCardProps) {
  const { textColor } = useCheckoutColors();

  const withImage = !!imageSrc;
  const withLabel = !!label;
  const withDescription = !!description;

  if (!withImage && !withLabel && !withDescription) {
    return null;
  }

  return (
    <Container $withDescription={withDescription}>
      <Show when={withImage}>
        <ItemImage
          src={imageSrc}
          alt={label || ''}
          mode="cover"
          containerWidth="60px"
          containerHeight="60px"
          withSkeleton
        />
      </Show>

      <Show when={withLabel || withDescription}>
        <Info>
          <Show when={withLabel}>
            <Typography
              textToken="text18Bold"
              className={createReadableClass({
                component: COMPONENT_NAME,
                element: 'label'
              })}
              color={textColor}
            >
              {label}
            </Typography>
          </Show>

          <Show when={withDescription}>
            <Typography
              textToken="text16"
              className={createReadableClass({
                component: COMPONENT_NAME,
                element: 'description'
              })}
              color={textColor}
            >
              {description}
            </Typography>
          </Show>
        </Info>
      </Show>
    </Container>
  );
}

const Container = styled.div.attrs<{ $withDescription: boolean }>({
  className: createReadableClass({
    component: COMPONENT_NAME
  })
})`
  display: flex;
  flex-direction: row;
  align-items: ${({ $withDescription }) =>
    $withDescription ? 'flex-start' : 'center'};
  gap: 16px;

  width: 100%;
`;

const ItemImage = styled(MediaImage).attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'image'
  })
})`
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
`;

const Info = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'info'
  })
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;

  min-width: 0;
`;
